import { createContext, FC, ReactNode, useCallback, useContext, useEffect } from "react";

import { Box, Stack } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { useFormContext, useFormState } from "react-hook-form";

import { useRatesExpansion } from "../../../RatesExpansion/RatesExpansionContext";
import { useRateContext } from "../../Context/RateContext";
import { RateExtrasToggle } from "../RateExtrasToggle/RateExtrasToggle";
import { RateSectionDivider } from "../RateSectionDivider/RateSectionDivider";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { getRateErrorEntries } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/helpers/rateErrors";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface ExtrasSectionProps {
  mode: "toggle" | "always-open";
  variant?: "trailing-toggle" | "options-panel";
  optionsLabel?: string;
  alwaysOpenDividerLabel?: string;
  collapsedSummary?: ReactNode;
  children: ReactNode;
}

const ExtrasSectionOpenContext = createContext<boolean>(false);

export const useIsExtrasSectionOpen = (): boolean => useContext(ExtrasSectionOpenContext);

export const ExtrasSection: FC<ExtrasSectionProps> = ({
  mode,
  variant = "trailing-toggle",
  optionsLabel,
  alwaysOpenDividerLabel,
  collapsedSummary,
  children,
}) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { rateId, rateIndex, formCategory } = useRateContext();
  const { control } = useFormContext<StepTicketsFormData>();
  const { errors, submitCount } = useFormState({ control });
  const { expandedRateId, setExpandedRateId } = useRatesExpansion();

  const isOpen = mode === "toggle" ? expandedRateId === rateId : true;

  const toggle = useCallback(() => {
    const nextOpen = expandedRateId !== rateId;
    if (nextOpen) {
      logAmplitudeEvent(AmplitudeEvents.TicketOptionsExpanded, { form_type: formCategory, is_liveform: true });
    }
    setExpandedRateId(nextOpen ? rateId : null);
  }, [logAmplitudeEvent, expandedRateId, rateId, formCategory, setExpandedRateId]);

  const rateErrors = errors?.rates;
  const firstErroredEntry = getRateErrorEntries(rateErrors).find(([, error]) =>
    Boolean(
      error?.attendeesByTicket ||
      error?.groupMembershipMaxMembers ||
      error?.receiptEligibleAmount ||
      error?.salesStartDate ||
      error?.salesEndDate ||
      error?.salesStartAfterRateId ||
      error?.memberOnlyMembershipRateIds ||
      error?.categoryIds ||
      error?.seats ||
      error?.rateMinimumNumberToBuy ||
      error?.rateMaximumNumberToBuy
    )
  );
  const shouldAutoOpen = firstErroredEntry !== undefined && firstErroredEntry[0] === rateIndex;

  useEffect(() => {
    if (mode === "toggle" && shouldAutoOpen && expandedRateId !== rateId) {
      setExpandedRateId(rateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoOpen, submitCount, mode]);

  const dividerLabel = alwaysOpenDividerLabel ?? t("dashboard", "ticketing.sectionDivider.additionalOptions");
  const optionsPanelLabel = optionsLabel ?? t("dashboard", "ticketing.ticketOptions");

  if (mode === "always-open") {
    return (
      <ExtrasSectionOpenContext.Provider value={true}>
        <Stack spacing={2}>
          <RateSectionDivider label={dividerLabel} />
          <Stack spacing={2}>{children}</Stack>
        </Stack>
      </ExtrasSectionOpenContext.Provider>
    );
  }

  const expandedContent = (
    <Collapse in={isOpen} timeout="auto" unmountOnExit={false}>
      <Box
        sx={(theme) => ({
          opacity: isOpen ? 1 : 0,
          transition: theme.transitions.create("opacity", {
            duration: theme.transitions.duration.shorter,
          }),
          transitionDelay: isOpen ? theme.transitions.duration.shortest + "ms" : "0ms",
        })}
      >
        {variant === "options-panel" ? (
          <Stack spacing={2}>
            <RateSectionDivider label={optionsPanelLabel} />
            {children}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <RateSectionDivider label={dividerLabel} />
            <Stack spacing={2}>{children}</Stack>
          </Stack>
        )}
      </Box>
    </Collapse>
  );

  if (variant === "options-panel") {
    return (
      <ExtrasSectionOpenContext.Provider value={isOpen}>
        <Stack spacing={1.5}>
          {expandedContent}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Box sx={{ flexShrink: 0 }}>
              <RateExtrasToggle isOpen={isOpen} onToggle={toggle} closedLabel={optionsPanelLabel} variant="outlined" />
            </Box>
            {!isOpen && <Box sx={{ minWidth: 0, flex: 1 }}>{collapsedSummary}</Box>}
          </Box>
        </Stack>
      </ExtrasSectionOpenContext.Provider>
    );
  }

  return (
    <ExtrasSectionOpenContext.Provider value={isOpen}>
      <Stack spacing={1.5}>
        {expandedContent}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>{!isOpen && collapsedSummary}</Box>
          <Box sx={{ flexShrink: 0 }}>
            <RateExtrasToggle isOpen={isOpen} onToggle={toggle} />
          </Box>
        </Box>
      </Stack>
    </ExtrasSectionOpenContext.Provider>
  );
};
